import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Hammer, Package, ChevronLeft, ChevronRight, Hash, X, MapPin, Sparkles, ArrowRight, ShieldCheck, GitCompare } from "lucide-react";
import { SectionLabel, openLeadModal } from "./_shared";

const VIDEO_CATEGORIES = [
  { id: "qc", label: "QC & Testing", icon: ShieldCheck },
  { id: "comparison", label: "BBC vs Generic", icon: GitCompare },
  { id: "oem", label: "OEM & Custom", icon: Sparkles },
  { id: "field", label: "Field Deployments", icon: MapPin },
  { id: "walkthrough", label: "Walkthrough", icon: Play },
  { id: "arm", label: "Boom Arm", icon: Hammer },
  { id: "unboxing", label: "Unboxing", icon: Package },
];

const VIDEOS_LIBRARY = [
  {
    id: "elite-vs-generic-150w",
    category: "comparison",
    title: "BBC ELITE 150W vs Generic 150W",
    desc: "Side-by-side torque and cycle test — our flagship ELITE 150W against a claimed 150W Chinese generic. See the difference in real load, not paper spec.",
    src: "/videos/elite-vs-generic-150w.mp4",
    poster: "/videos/elite-vs-generic-150w-poster.jpg",
  },
  {
    id: "qc-barrier-bbc-319r",
    category: "qc",
    title: "Barrier QC Test · BBC-319R",
    desc: "Full pre-dispatch QC of unit BBC-319R — motor, controller, remote pairing and cycle test recorded and QR-bound.",
    src: "https://bbc-trust.s3.ap-southeast-2.amazonaws.com/Park+Smart/BBC-319R.mp4",
    poster: "/videos/qc-barrier-poster.jpg",
    serial: "BBC-319R",
  },
  {
    id: "qc-boom-arm-t90",
    category: "qc",
    title: "Boom Arm QC · BB T-90",
    desc: "Boom arm assembly test — extension, stress, and finish inspection before it ships with the barrier.",
    src: "https://bbc-trust.s3.ap-southeast-2.amazonaws.com/BBC+BOOM/BB+T-90.mp4",
    poster: "/videos/qc-boom-arm-poster.jpg",
    serial: "BB T-90",
  },
  {
    id: "factory-tour",
    category: "walkthrough",
    title: "Factory Line Walkthrough",
    desc: "Inside the BBC production floor — motor, controller, SMPS and remote laid out per unit before packaging.",
    src: "/videos/factory-tour.mp4",
    poster: "/videos/factory-tour-poster.jpg",
  },
  {
    id: "field-01",
    category: "oem",
    title: "OEM Customisation",
    desc: "Full custom-branded cabinet with LED signage — showcasing our OEM & white-label capability for enterprise clients.",
    src: "/videos/field-01.mp4",
    poster: "/videos/field-01-poster.jpg",
  },
  {
    id: "field-02",
    category: "field",
    title: "Live Lane · High-Frequency Access",
    desc: "BBC barrier cycling through peak-hour traffic — sub-second response on a commercial lane.",
    src: "/videos/field-02.mp4",
    poster: "/videos/field-02-poster.jpg",
  },
  {
    id: "field-03",
    category: "walkthrough",
    title: "Glass Finish Barrier · Demo",
    desc: "Glass-front cabinet finish walkthrough — architectural presence with the same BBC engineered mechanics inside.",
    src: "/videos/field-03.mp4",
    poster: "/videos/field-03-poster.jpg",
  },
  {
    id: "field-04",
    category: "oem",
    title: "OEM Customisation · Custom Graphics",
    desc: "Complex full-cabinet graphics printed to spec — turn any barrier into brand real estate for your site.",
    src: "/videos/field-04.mp4",
    poster: "/videos/field-04-poster.jpg",
  },
  {
    id: "telescopic-arm",
    category: "arm",
    title: "Telescopic Boom Arm",
    desc: "How our 6m × 6kg telescopic arm extends and locks.",
    src: "/videos/telescopic-arm.mp4",
    poster: "/videos/telescopic-arm-poster.jpg",
  },
  {
    id: "round-arm",
    category: "arm",
    title: "Round Boom Arm",
    desc: "3m × 3kg round arm — speed-optimised for the FLASH family.",
    src: "/videos/round-arm.mp4",
    poster: "/videos/round-arm-poster.jpg",
  },
  { id: "elite-walkthrough", category: "walkthrough", title: "ELITE Walkthrough", desc: "Commercial-duty walkthrough — ELITE 150W family.", src: "/videos/elite-walkthrough.mp4", poster: "/videos/elite-walkthrough-poster.jpg" },
  { id: "flash-walkthrough", category: "walkthrough", title: "FLASH Walkthrough", desc: "Sub-second cycle — FLASH 180W lane deployment.", src: "/videos/flash-walkthrough.mp4", poster: "/videos/flash-walkthrough-poster.jpg" },
  { id: "unboxing", category: "unboxing", title: "Barrier Unboxing", desc: "What arrives when you order a BBC barrier.", src: "/videos/barrier-unboxing.mp4", poster: "/videos/barrier-unboxing-poster.jpg" },
];

const isVideoReady = (v) => Boolean(v && v.src);

/* Play-facade: div (NOT button) so it can safely nest inside the parent card <button>.
   Uses role="button" + tabIndex + keyboard handler for a11y — click and hover both trigger. */
const FieldVideoThumbnail = ({ src, poster, alt, className = "" }) => {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const PREVIEW_TIME = 6;

  const activate = () => {
    setLoaded(true);
    setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }, 30);
  };

  if (!loaded) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Play ${alt}`}
        onClick={(e) => { e.stopPropagation(); activate(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); activate(); } }}
        onMouseEnter={activate}
        className={`${className} group relative overflow-hidden bg-gradient-to-br from-[#0F1113] via-[#13161A] to-[#0F1113] cursor-pointer`}
      >
        {poster && (
          <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1113]/40 via-transparent to-transparent" />
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          background: "repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.4) 2px 3px)",
        }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#00FF85]/30 blur-xl group-hover:blur-2xl transition-all" />
            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00FF85] flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_40px_rgba(0,255,133,0.5)]">
              <Play size={22} className="text-black translate-x-0.5" fill="currentColor" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-white/70 group-hover:text-[#00FF85] transition">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
          Tap to play
        </div>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      src={src}
      poster={poster || undefined}
      muted
      loop
      playsInline
      autoPlay
      controls
      preload="auto"
      onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = PREVIEW_TIME; } catch (_) { /* ignore */ } }}
      className={className}
      aria-label={alt}
    />
  );
};

export const FieldEvidenceVideos = () => {
  const [activeCat, setActiveCat] = useState("all");
  const [openVideo, setOpenVideo] = useState(null);
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const filtered = useMemo(() => {
    if (activeCat === "all") return VIDEOS_LIBRARY;
    return VIDEOS_LIBRARY.filter((v) => v.category === activeCat);
  }, [activeCat]);

  const readyCount = VIDEOS_LIBRARY.filter(isVideoReady).length;

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [filtered.length]);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-field-video-card]");
    const step = card ? card.getBoundingClientRect().width + 12 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section id="videos" data-testid="platform-videos" className="relative py-20 md:py-28 border-t border-white/5 bg-[#13161A] overflow-hidden">
      <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 mb-10">
        <SectionLabel id="//" label="Factory Evidence" />
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
            Watch The Hardware
            <br />
            <span className="text-[#00FF85]">Actually Move.</span>
          </h2>
          <div className="md:max-w-md">
            <p className="text-[#A1A1AA] text-sm md:text-base font-body">
              Recorded inside our factory line. Every clip is bound to a serial number — when you scan a barrier's QR, the same footage shows up on the unit's record.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
              {readyCount} of {VIDEOS_LIBRARY.length} clips live · more being indexed
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCat("all")}
            data-testid="platform-videos-filter-all"
            className={`px-4 py-2 border font-mono text-[10px] uppercase tracking-[0.22em] transition ${
              activeCat === "all"
                ? "bg-[#00FF85] border-[#00FF85] text-black"
                : "border-white/15 text-white/70 hover:border-[#00FF85]/60 hover:text-[#00FF85]"
            }`}
          >
            All · {VIDEOS_LIBRARY.length}
          </button>
          {VIDEO_CATEGORIES.map((c) => {
            const count = VIDEOS_LIBRARY.filter((v) => v.category === c.id).length;
            const Icon = c.icon;
            const isActive = activeCat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`platform-videos-filter-${c.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 border font-mono text-[10px] uppercase tracking-[0.22em] transition ${
                  isActive
                    ? "bg-[#00FF85] border-[#00FF85] text-black"
                    : "border-white/15 text-white/70 hover:border-[#00FF85]/60 hover:text-[#00FF85]"
                }`}
              >
                <Icon size={12} />
                {c.label} · {count}
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          disabled={!canPrev}
          data-testid="platform-videos-prev"
          aria-label="Previous"
          className={`hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center border transition ${
            canPrev
              ? "bg-[#0F1113]/90 border-white/20 text-white hover:border-[#00FF85] hover:text-[#00FF85]"
              : "bg-[#0F1113]/50 border-white/5 text-white/20 cursor-not-allowed"
          }`}
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          disabled={!canNext}
          data-testid="platform-videos-next"
          aria-label="Next"
          className={`hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center border transition ${
            canNext
              ? "bg-[#0F1113]/90 border-white/20 text-white hover:border-[#00FF85] hover:text-[#00FF85]"
              : "bg-[#0F1113]/50 border-white/5 text-white/20 cursor-not-allowed"
          }`}
        >
          <ChevronRight size={20} />
        </button>

        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-[#13161A] to-transparent z-[1]" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#13161A] to-transparent z-[1]" />

        <div
          ref={scrollerRef}
          data-testid="platform-videos-scroller"
          className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth no-scrollbar px-6 md:px-12"
          style={{ scrollbarWidth: "none" }}
        >
          {filtered.map((v, idx) => {
            const ready = isVideoReady(v);
            const cat = VIDEO_CATEGORIES.find((c) => c.id === v.category);
            const CatIcon = cat?.icon || Play;
            return (
              <button
                key={v.id}
                type="button"
                data-field-video-card
                onClick={() => ready && setOpenVideo(v)}
                disabled={!ready}
                data-testid={`platform-video-card-${v.id}`}
                className={`group relative shrink-0 snap-start w-[85%] sm:w-[55%] lg:w-[36%] border text-left transition bg-[#16191C] ${
                  ready
                    ? "border-white/10 hover:border-[#00FF85]/50 cursor-pointer"
                    : "border-white/5 cursor-not-allowed opacity-80"
                }`}
              >
                <div className="relative aspect-video bg-gradient-to-br from-[#00FF85]/15 via-[#16191C] to-black flex items-center justify-center overflow-hidden">
                  {ready ? (
                    <FieldVideoThumbnail src={v.src} poster={v.poster} alt={v.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <CatIcon size={56} className="text-[#00FF85]/30" strokeWidth={1.2} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

                  <span className="absolute top-3 left-3 w-3 h-3 border-l border-t border-[#00FF85] pointer-events-none" />
                  <span className="absolute top-3 right-3 w-3 h-3 border-r border-t border-[#00FF85] pointer-events-none" />
                  <span className="absolute bottom-3 left-3 w-3 h-3 border-l border-b border-[#00FF85] pointer-events-none" />
                  <span className="absolute bottom-3 right-3 w-3 h-3 border-r border-b border-[#00FF85] pointer-events-none" />

                  {!ready && (
                    <span className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.22em] text-[#B0FFD9] bg-black/60 border border-[#B0FFD9]/30 px-2 py-1 pointer-events-none">
                      Indexing
                    </span>
                  )}

                  <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-white/80 bg-black/60 backdrop-blur-sm px-2 py-1 border border-white/10 pointer-events-none">
                    <CatIcon size={10} />
                    {cat?.label}
                  </span>
                </div>

                <div className="p-5 border-t border-white/5 bg-[#181B1E]">
                  <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#00FF85]">
                    <Hash size={10} />
                    EVID.{String(idx + 1).padStart(3, "0")}
                  </div>
                  <h3 className="font-display mt-1.5 text-white text-base md:text-lg font-bold tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-1.5 text-[#A1A1AA] text-sm font-body leading-relaxed">{v.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        <p className="md:hidden mt-3 text-center text-white/40 text-xs font-mono uppercase tracking-[0.22em]">Swipe →</p>
      </div>

      {/* OEM & Custom Branding callout */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 mt-16 md:mt-20">
        <div
          data-testid="oem-callout"
          className="relative border border-[#00FF85]/30 bg-[#16191C] overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF85]/60 to-transparent" />
          <div
            className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(0,255,133,0.12), transparent 60%)" }}
          />

          <div className="relative grid lg:grid-cols-12 gap-8 items-center p-8 md:p-12">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
                <Sparkles size={12} />
                OEM & Custom Branding
              </div>
              <h3 className="mt-4 font-display text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
                Your brand.
                <br />
                <span className="text-[#A1A1AA]">Our engineering.</span>
              </h3>
              <p className="mt-5 max-w-2xl text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">
                Every BBC barrier can be built to your visual identity — custom-branded cabinets, full-graphic wraps, LED signage,
                and colour finishes. Same BBC Trust platform inside. Ideal for hotel groups, retail chains, gated communities,
                toll operators and dealer-white-label programs.
              </p>

              <div className="mt-6 grid sm:grid-cols-3 gap-2 max-w-2xl">
                {[
                  { t: "Custom cabinet colours", d: "Any RAL powder-coat or brand palette." },
                  { t: "Graphic & logo wraps", d: "Full-cabinet complex graphics to spec." },
                  { t: "White-label options", d: "Ship under your dealer brand." },
                ].map((f) => (
                  <div key={f.t} className="border border-white/10 bg-white/[0.02] px-3 py-3">
                    <div className="font-display text-white text-sm font-bold tracking-tight">{f.t}</div>
                    <div className="mt-1 text-[#A1A1AA] text-xs font-body leading-snug">{f.d}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 flex lg:justify-end">
              <button
                type="button"
                onClick={() => openLeadModal("sales", "oem-callout")}
                data-testid="oem-callout-cta"
                className="group inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-4 text-xs uppercase tracking-wider transition-all shadow-[0_0_24px_rgba(0,255,133,0.35)] hover:shadow-[0_0_36px_rgba(0,255,133,0.6)]"
              >
                Discuss a custom build
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {openVideo && <PlatformVideoLightbox video={openVideo} onClose={() => setOpenVideo(null)} />}
    </section>
  );
};

const PlatformVideoLightbox = ({ video, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div data-testid="platform-video-lightbox" className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/92 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-5xl overflow-hidden border border-[#00FF85]/30 bg-[#0F1113] shadow-2xl shadow-[#00FF85]/10">
        <span className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-[#00FF85] z-10" />
        <span className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-[#00FF85] z-10" />
        <span className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-[#00FF85] z-10" />
        <span className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-[#00FF85] z-10" />

        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#16191C]">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] bbc-pulse" />
              Factory Evidence · Playing
            </div>
            <h3 className="mt-1 font-display text-white font-bold tracking-tight">{video.title}</h3>
            <p className="text-[#A1A1AA] text-xs mt-0.5 font-body">{video.desc}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="platform-video-close"
            aria-label="Close"
            className="w-9 h-9 border border-white/10 hover:border-[#00FF85] text-white/70 hover:text-[#00FF85] flex items-center justify-center transition"
          >
            <X size={18} />
          </button>
        </div>
        <video src={video.src} controls autoPlay playsInline className="w-full aspect-video bg-black" />
      </div>
    </div>
  );
};
